import { useState, useCallback } from 'react';
import axiosClient from './lib/axiosClient';

const FIELD_TEMPLATES = {
  grinding: [
    { key: 'disk_size',      label: 'Disk Size',      type: 'select',      options: ['7in','10in','17in','20in','32in'] },
    { key: 'disk_count',     label: 'Disk Count',     type: 'select',      options: ['1','3','6','9'] },
    { key: 'disk_type',      label: 'Disk Type',      type: 'select',      options: ['PCD','Hybrid','Resin','Metal Bond'] },
    { key: 'drive_type',     label: 'Drive Type',     type: 'select',      options: ['Single','Planetary'] },
    { key: 'voltage',        label: 'Voltage',        type: 'select',      options: ['110V','220V'] },
    { key: 'propulsion',     label: 'Propulsion',     type: 'select',      options: ['Push','Ride-on'] },
    { key: 'self_propelled', label: 'Self Propelled', type: 'yesno' },
    { key: 'water_hookup',   label: 'Water Hookup',   type: 'yesno' },
    { key: 'weight_lbs',     label: 'Weight',         type: 'measurement', unit: 'lbs' },
    { key: 'max_rpm',        label: 'Max RPM',        type: 'number' },
  ],
  scarif: [
    { key: 'cutting_width',  label: 'Cutting Width',  type: 'measurement', unit: 'in' },
    { key: 'drum_type',      label: 'Drum Type',      type: 'select',      options: ['Flail','Carbide','Multi-tooth'] },
    { key: 'voltage',        label: 'Voltage',        type: 'select',      options: ['110V','220V'] },
    { key: 'self_propelled', label: 'Self Propelled', type: 'yesno' },
    { key: 'weight_lbs',     label: 'Weight',         type: 'measurement', unit: 'lbs' },
  ],
  blast: [
    { key: 'blast_width',    label: 'Blast Width',    type: 'measurement', unit: 'in' },
    { key: 'shot_size',      label: 'Shot Size',      type: 'select',      options: ['S110','S170','S230','S280','S330'] },
    { key: 'self_propelled', label: 'Self Propelled', type: 'yesno' },
    { key: 'voltage',        label: 'Voltage',        type: 'select',      options: ['110V','220V'] },
    { key: 'weight_lbs',     label: 'Weight',         type: 'measurement', unit: 'lbs' },
  ],
  polish: [
    { key: 'pad_size',       label: 'Pad Size',       type: 'select',      options: ['17in','20in','27in','32in'] },
    { key: 'pad_type',       label: 'Pad Type',       type: 'select',      options: ['Diamond','Resin','Spray'] },
    { key: 'voltage',        label: 'Voltage',        type: 'select',      options: ['110V','220V'] },
    { key: 'water_fed',      label: 'Water Fed',      type: 'yesno' },
    { key: 'weight_lbs',     label: 'Weight',         type: 'measurement', unit: 'lbs' },
  ],
  dust: [
    { key: 'cfm',            label: 'CFM',            type: 'number' },
    { key: 'bag_count',      label: 'Bag Count',      type: 'select',      options: ['1','2','3'] },
    { key: 'hepa',           label: 'HEPA Filter',    type: 'yesno' },
    { key: 'voltage',        label: 'Voltage',        type: 'select',      options: ['110V','220V'] },
    { key: 'inlet_size',     label: 'Inlet Size',     type: 'measurement', unit: 'in' },
  ],
  cut: [
    { key: 'blade_size',     label: 'Blade Size',     type: 'measurement', unit: 'in' },
    { key: 'cut_depth',      label: 'Cut Depth',      type: 'measurement', unit: 'in' },
    { key: 'blade_type',     label: 'Blade Type',     type: 'select',      options: ['Diamond','Abrasive','Carbide'] },
    { key: 'voltage',        label: 'Voltage',        type: 'select',      options: ['110V','220V','Gas'] },
    { key: 'self_propelled', label: 'Self Propelled', type: 'yesno' },
  ],
  applic: [
    { key: 'application_type', label: 'Application Type', type: 'select', options: ['Epoxy','Urethane','Densifier','Hardener'] },
    { key: 'spread_width',   label: 'Spread Width',   type: 'measurement', unit: 'in' },
    { key: 'tank_size',      label: 'Tank Size',      type: 'measurement', unit: 'gal' },
    { key: 'self_propelled', label: 'Self Propelled', type: 'yesno' },
  ],
};

function getSuggestedFields(categoryName) {
  const lower = categoryName.toLowerCase();
  for (const [keyword, fields] of Object.entries(FIELD_TEMPLATES)) {
    if (lower.includes(keyword)) return fields;
  }
  return [];
}

function CategoryNode({ cat, allCats, products, depth = 0, selectedId, onSelect, onRefresh, mode, onSelectProduct, activeProductId, draggedId, dragOverId, onDragStart, onDragOverNode, onDragLeaveNode, onDropNode, onDragEndNode }) {
  const [expanded, setExpanded] = useState(depth === 0);
  const [editing, setEditing]   = useState(false);
  const [editName, setEditName] = useState(cat.name);
  const [adding, setAdding]     = useState(false);
  const [newName, setNewName]   = useState('');
  const [saving, setSaving]     = useState(false);

  const children   = allCats.filter(c => c.parent_id === cat.id);
  const catProds   = products.filter(p => p.category_id === cat.id);
  const totalUnits = catProds.reduce((s, p) => s + (p.variants?.length || p.quantity_total || 0), 0);
  const availUnits = catProds.reduce((s, p) => s + (p.variants?.filter(v => v.unit_status === 'available').length || p.quantity_available || 0), 0);
  const hasChildren = children.length > 0 || catProds.length > 0;

  const handleRename = async () => {
    if (!editName.trim() || editName === cat.name) { setEditing(false); return; }
    setSaving(true);
    try {
      await axiosClient.patch(`/api/rentals/categories/${cat.id}`, { name: editName.trim() });
      onRefresh();
    } catch(e) {}
    setSaving(false);
    setEditing(false);
  };

  const handleAddSub = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await axiosClient.post('/api/rentals/categories', {
        name: newName.trim(),
        parent_id: cat.id,
        custom_field_schema: getSuggestedFields(newName),
      });
      onRefresh();
      setNewName('');
      setAdding(false);
      setExpanded(true);
    } catch(e) {}
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${cat.name}"? All products will be moved to Uncategorized.`)) return;
    try {
      // Move all products to uncategorized first
      await axiosClient.patch(`/api/rentals/categories/${cat.id}/unassign-products`).catch(() => {});
      await axiosClient.delete(`/api/rentals/categories/${cat.id}`);
      onRefresh();
    } catch(e) {
      alert(e?.response?.data?.error || 'Cannot delete');
    }
  };

  const isSelected = selectedId === cat.id;

  return (
    <div style={{ marginLeft: depth * 16 }}>
      <div
        draggable={mode === 'manage'}
        onDragStart={mode === 'manage' ? (e => onDragStart(e, cat)) : undefined}
        onDragOver={mode === 'manage' ? (e => onDragOverNode(e, cat)) : undefined}
        onDragLeave={mode === 'manage' ? (() => onDragLeaveNode(cat)) : undefined}
        onDrop={mode === 'manage' ? (e => onDropNode(e, cat)) : undefined}
        onDragEnd={mode === 'manage' ? onDragEndNode : undefined}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
          background: isSelected ? '#1a1a3a' : 'transparent',
          border: dragOverId === cat.id ? '1px dashed #f97316' : (isSelected ? '1px solid #3a3aff' : '1px solid transparent'),
          marginBottom: 2,
          opacity: draggedId === cat.id ? 0.4 : 1,
        }}
        onClick={() => { onSelect(cat); if (hasChildren) setExpanded(e => !e); }}
      >
        <span style={{ fontSize: 11, color: '#555', width: 12, flexShrink: 0 }}>
          {hasChildren ? (expanded ? '▼' : '▶') : '·'}
        </span>
        <span style={{ fontSize: 14 }}>{depth === 0 ? '📦' : '📁'}</span>

        {editing ? (
          <input
            autoFocus
            style={{ ...S.input, flex: 1, padding: '2px 6px', fontSize: 13 }}
            value={editName}
            onChange={e => setEditName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditing(false); }}
            onBlur={handleRename}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span style={{ flex: 1, fontSize: 13, color: isSelected ? '#fff' : '#ddd', fontWeight: isSelected ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {cat.name}
          </span>
        )}

        {totalUnits > 0 && !editing && (
          <span style={{ fontSize: 10, color: '#4ade80', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            {availUnits}/{totalUnits}
          </span>
        )}

        {mode === 'manage' && !editing && (
          <div style={{ display: 'flex', gap: 3, marginLeft: 4 }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setAdding(v => !v)} style={S.iconBtn} title="Add subcategory">+</button>
            <button onClick={() => { setEditing(true); setEditName(cat.name); }} style={S.iconBtn} title="Rename">✎</button>
            <button onClick={handleDelete} style={{ ...S.iconBtn, color: '#f87171' }} title="Delete">✕</button>
          </div>
        )}
      </div>

      {adding && (
        <div style={{ marginLeft: (depth + 1) * 16 + 8, marginBottom: 4, display: 'flex', gap: 6 }}>
          <input
            autoFocus
            style={{ ...S.input, flex: 1, padding: '4px 8px', fontSize: 12 }}
            placeholder="Subcategory name..."
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAddSub(); if (e.key === 'Escape') setAdding(false); }}
          />
          <button onClick={handleAddSub} disabled={saving || !newName.trim()} style={S.addBtn}>
            {saving ? '...' : 'Add'}
          </button>
        </div>
      )}

      {expanded && children.map(child => (
        <CategoryNode
          key={child.id}
          cat={child}
          allCats={allCats}
          products={products}
          depth={depth + 1}
          selectedId={selectedId}
          onSelect={onSelect}
          onRefresh={onRefresh}
          mode={mode}
          onSelectProduct={onSelectProduct}
          activeProductId={activeProductId}
          draggedId={draggedId}
          dragOverId={dragOverId}
          onDragStart={onDragStart}
          onDragOverNode={onDragOverNode}
          onDragLeaveNode={onDragLeaveNode}
          onDropNode={onDropNode}
          onDragEndNode={onDragEndNode}
        />
      ))}
      {expanded && catProds.map(p => (
        <div
          key={p.id}
          style={{
            marginLeft: (depth + 1) * 16 + 8,
            padding: '3px 8px',
            fontSize: 12,
            color: activeProductId === p.id ? '#fff' : '#666',
            background: activeProductId === p.id ? '#1a1a3a' : 'transparent',
            borderRadius: 4,
            cursor: onSelectProduct ? 'pointer' : 'default',
            marginBottom: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          onClick={e => { e.stopPropagation(); onSelectProduct && onSelectProduct(p); }}
        >
          · {p.name}
        </div>
      ))}
    </div>
  );
}

export default function CategoryTree({
  categories = [],
  products = [],
  selectedId = null,
  onSelect,
  onRefresh,
  mode = 'browse',
  showUncategorized = true,
  onSelectProduct = null,
  activeProductId = null,
}) {
  const [addingRoot, setAddingRoot] = useState(false);
  const [rootName, setRootName]     = useState('');
  const [saving, setSaving]         = useState(false);
  const [draggedId, setDraggedId]   = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const rootCats = categories.filter(c => !c.parent_id);
  const uncategorizedProds = products.filter(p => !p.category_id);

  const moveCategory = async (draggedCatId, newParentId, beforeId) => {
    const dragged = categories.find(c => c.id === draggedCatId);
    if (!dragged || draggedCatId === beforeId) return;
    const parentKey = newParentId || null;

    const destSiblings = categories
      .filter(c => (c.parent_id || null) === parentKey && c.id !== draggedCatId)
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

    let insertAt = destSiblings.length;
    if (beforeId) {
      const idx = destSiblings.findIndex(c => c.id === beforeId);
      if (idx !== -1) insertAt = idx;
    }
    const ordered = [...destSiblings.slice(0, insertAt), dragged, ...destSiblings.slice(insertAt)];
    const updates = ordered.map((c, i) => ({ id: c.id, parent_id: parentKey, display_order: i }));

    try {
      await Promise.all(updates.map(u =>
        axiosClient.patch(`/api/rentals/categories/${u.id}`, { parent_id: u.parent_id, display_order: u.display_order })
      ));
    } catch (e) {
      console.error('Failed to save category order', e);
    } finally {
      onRefresh && onRefresh();
    }
  };

  const handleDragStart = (e, cat) => {
    setDraggedId(cat.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', cat.id);
  };

  const handleDragOverNode = (e, cat) => {
    if (!draggedId || draggedId === cat.id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverId !== cat.id) setDragOverId(cat.id);
  };

  const handleDragLeaveNode = (cat) => {
    setDragOverId(prev => (prev === cat.id ? null : prev));
  };

  const handleDropNode = (e, targetCat) => {
    e.preventDefault();
    setDragOverId(null);
    const draggedCatId = draggedId;
    setDraggedId(null);
    if (!draggedCatId || draggedCatId === targetCat.id) return;
    const dragged = categories.find(c => c.id === draggedCatId);
    if (!dragged) return;

    const draggedIsRoot = !dragged.parent_id;
    const targetIsRoot = !targetCat.parent_id;

    if (draggedIsRoot) {
      if (!targetIsRoot) return; // parent categories only reorder among other parents
      moveCategory(draggedCatId, null, targetCat.id);
    } else if (targetIsRoot) {
      moveCategory(draggedCatId, targetCat.id, null); // move into this parent, append at end
    } else {
      moveCategory(draggedCatId, targetCat.parent_id, targetCat.id); // reorder / move between parents
    }
  };

  const handleDragEndNode = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleAddRoot = async () => {
    if (!rootName.trim()) return;
    setSaving(true);
    try {
      await axiosClient.post('/api/rentals/categories', {
        name: rootName.trim(),
        custom_field_schema: getSuggestedFields(rootName),
      });
      onRefresh && onRefresh();
      setRootName('');
      setAddingRoot(false);
    } catch(e) {}
    setSaving(false);
  };

  return (
    <div style={S.wrap}>
      {rootCats.map(cat => (
        <CategoryNode
          key={cat.id}
          cat={cat}
          allCats={categories}
          products={products}
          depth={0}
          selectedId={selectedId}
          onSelect={onSelect}
          onRefresh={onRefresh}
          mode={mode}
          onSelectProduct={onSelectProduct}
          activeProductId={activeProductId}
          draggedId={draggedId}
          dragOverId={dragOverId}
          onDragStart={handleDragStart}
          onDragOverNode={handleDragOverNode}
          onDragLeaveNode={handleDragLeaveNode}
          onDropNode={handleDropNode}
          onDragEndNode={handleDragEndNode}
        />
      ))}

      {showUncategorized && uncategorizedProds.length > 0 && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
            background: selectedId === '__uncategorized__' ? '#1a1a3a' : 'transparent',
            border: selectedId === '__uncategorized__' ? '1px solid #3a3aff' : '1px solid transparent',
            marginBottom: 2, marginTop: 4,
          }}
          onClick={() => onSelect({ id: '__uncategorized__', name: 'Uncategorized' })}
        >
          <span style={{ fontSize: 11, color: '#555', width: 12 }}>·</span>
          <span style={{ fontSize: 14 }}>📂</span>
          <span style={{ flex: 1, fontSize: 13, color: '#888', fontStyle: 'italic' }}>Uncategorized</span>
          <span style={{ fontSize: 10, color: '#888' }}>{uncategorizedProds.length}</span>
        </div>
      )}

      {mode === 'manage' && (
        <div style={{ marginTop: 8 }}>
          {addingRoot ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                autoFocus
                style={{ ...S.input, flex: 1, padding: '5px 8px', fontSize: 12 }}
                placeholder="New category name..."
                value={rootName}
                onChange={e => setRootName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddRoot(); if (e.key === 'Escape') setAddingRoot(false); }}
              />
              <button onClick={handleAddRoot} disabled={saving || !rootName.trim()} style={S.addBtn}>
                {saving ? '...' : 'Add'}
              </button>
              <button onClick={() => setAddingRoot(false)} style={S.cancelBtn}>✕</button>
            </div>
          ) : (
            <button onClick={() => setAddingRoot(true)} style={S.newCatBtn}>
              + New Category
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const S = {
  wrap: { padding: '4px 0' },
  input: {
    background: '#0f0f20', border: '1px solid #2a2a4a', borderRadius: 5,
    color: '#e0e0f0', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box',
  },
  iconBtn: {
    background: 'none', border: 'none', color: '#555', cursor: 'pointer',
    fontSize: 13, padding: '0 3px', lineHeight: 1,
  },
  addBtn: {
    background: '#2a2a5a', border: 'none', color: '#a0a0ff',
    borderRadius: 5, padding: '4px 10px', fontSize: 12, cursor: 'pointer', flexShrink: 0,
  },
  cancelBtn: {
    background: 'none', border: 'none', color: '#666',
    cursor: 'pointer', fontSize: 13, padding: '4px 6px',
  },
  newCatBtn: {
    background: 'none', border: '1px dashed #2a2a4a', color: '#5a5a8a',
    borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', width: '100%',
    textAlign: 'left',
  },
};
